import { cn } from '@/utils/cn'
import { mergeAttributes, Node } from '@tiptap/core'
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { ImagePlus, Trash2, X } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

const MAX_IMAGES = 6

export interface GalleryImage {
  src: string
  alt?: string
}

export interface GalleryExtensionOptions {
  onUpload?: (file: File) => Promise<string>
  validateFn?: (file: File) => boolean
}

export type GalleryMode = 'normal' | 'wide' | 'full'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    gallery: {
      insertGallery: (attrs?: { images?: GalleryImage[]; mode?: GalleryMode }) => ReturnType
      updateGalleryImages: (images: GalleryImage[]) => ReturnType
    }
  }
}

// Grid layout calculation based on number of images
function getGridLayout(count: number): { cols: number; rows: number } {
  if (count <= 0) return { cols: 1, rows: 1 }
  if (count === 1) return { cols: 1, rows: 1 }
  if (count === 2) return { cols: 2, rows: 1 }
  if (count === 3) return { cols: 3, rows: 1 }
  if (count === 4) return { cols: 2, rows: 2 }
  if (count === 5) return { cols: 3, rows: 2 } // 3 top, 2 bottom
  return { cols: 3, rows: 2 } // 6 images: 3x2 grid
}

// React Component for the Gallery Node View
const GalleryNodeComponent: React.FC<NodeViewProps> = ({
  node,
  selected,
  updateAttributes,
  extension,
}) => {
  const images: GalleryImage[] = node.attrs.images || []
  const galleryMode = node.attrs.mode || 'wide'
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)

  const { onUpload, validateFn } = extension.options as GalleryExtensionOptions

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return
      if (!onUpload) {
        toast.error('Image upload not configured')
        return
      }

      const remainingSlots = MAX_IMAGES - images.length
      const filesToUpload = Array.from(files).slice(0, remainingSlots)

      if (files.length > remainingSlots) {
        toast.warning(
          `Only ${remainingSlots} more image${remainingSlots > 1 ? 's' : ''} can be added. Extra images were ignored.`,
        )
      }

      setIsUploading(true)
      setUploadProgress(0)

      const newImages: GalleryImage[] = []
      let completed = 0

      for (const file of filesToUpload) {
        // Validate file
        if (validateFn && !validateFn(file)) {
          continue
        }

        try {
          const url = await onUpload(file)
          newImages.push({ src: url, alt: file.name })
          completed++
          setUploadProgress(Math.round((completed / filesToUpload.length) * 100))
        } catch (error) {
          console.error('Failed to upload image:', error)
          toast.error(`Failed to upload ${file.name}`)
        }
      }

      if (newImages.length > 0) {
        updateAttributes({ images: [...images, ...newImages] })
      }

      setIsUploading(false)
      setUploadProgress(0)
    },
    [images, onUpload, validateFn, updateAttributes],
  )

  const handleRemoveImage = useCallback(
    (index: number) => {
      const newImages = images.filter((_, i) => i !== index)
      updateAttributes({ images: newImages })
    },
    [images, updateAttributes],
  )

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const { cols } = getGridLayout(images.length)

  // Empty state - show upload prompt
  if (images.length === 0) {
    return (
      <NodeViewWrapper
        className={cn('node-gallery', {
          'ring-2 ring-primary rounded-lg': selected,
        })}
        data-gallery-node="true"
        data-width={galleryMode}
      >
        <div
          className="border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary hover:bg-accent/50 transition-colors"
          onClick={openFilePicker}
        >
          <div className="flex flex-col items-center justify-center text-center">
            <ImagePlus className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-foreground mb-1">
              {isUploading ? 'Uploading...' : 'Select up to 6 images'}
            </p>
            <p className="text-sm text-muted-foreground">
              {isUploading
                ? `${uploadProgress}% complete`
                : 'Click to browse or drag and drop'}
            </p>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
        />
      </NodeViewWrapper>
    )
  }

  // Gallery grid view
  return (
    <NodeViewWrapper
      className={cn('node-gallery', {
        'ring-2 ring-primary rounded-lg': selected,
      })}
      data-gallery-node="true"
      data-width={galleryMode}
    >
      <div className="relative">
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
          }}
        >
          {images.map((image, index) => (
            <div key={index} className="relative group aspect-square">
              <img
                src={image.src}
                alt={image.alt || `Gallery image ${index + 1}`}
                className="w-full h-full object-cover rounded-lg"
              />
              {/* Remove button on hover */}
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove image"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}

          {/* Add more images button if under limit */}
          {images.length < MAX_IMAGES && (
            <div
              className="aspect-square border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-accent/50 transition-colors flex items-center justify-center"
              onClick={openFilePicker}
            >
              <div className="flex flex-col items-center text-muted-foreground">
                <ImagePlus className="w-8 h-8 mb-1" />
                <span className="text-xs">
                  {isUploading ? `${uploadProgress}%` : 'Add more'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Gallery info bar */}
        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span>
            {images.length} of {MAX_IMAGES} images
          </span>
          {images.length > 0 && (
            <button
              type="button"
              onClick={() => updateAttributes({ images: [] })}
              className="flex items-center gap-1 text-destructive hover:text-destructive/80 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Clear all
            </button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
      />
    </NodeViewWrapper>
  )
}

// TipTap Extension Definition
export const GalleryExtension = Node.create<GalleryExtensionOptions>({
  name: 'gallery',

  group: 'block',

  atom: true,

  addOptions() {
    return {
      onUpload: undefined,
      validateFn: undefined,
    }
  },

  addAttributes() {
    return {
      images: {
        default: [],
        parseHTML: (element) => {
          const imagesAttr = element.getAttribute('data-images')
          if (imagesAttr) {
            try {
              return JSON.parse(imagesAttr)
            } catch {
              return []
            }
          }
          return []
        },
        renderHTML: (attributes) => {
          return {
            'data-images': JSON.stringify(attributes.images || []),
          }
        },
      },
      mode: {
        default: 'wide',
        parseHTML: (element) => element.getAttribute('data-width') || 'wide',
        renderHTML: (attributes) => {
          return {
            'data-width': attributes.mode || 'wide',
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="gallery"]',
        getAttrs: (element) => {
          const domElement = element as HTMLElement
          const imagesAttr = domElement.getAttribute('data-images')
          let images: GalleryImage[] = []
          if (imagesAttr) {
            try {
              images = JSON.parse(imagesAttr)
            } catch {
              images = []
            }
          }
          return { images }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const images = node.attrs.images || []
    const mode = node.attrs.mode || 'wide'

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'gallery',
        'data-component': 'gallery',
        'data-images': JSON.stringify(images),
        'data-width': mode,
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(GalleryNodeComponent)
  },

  addCommands() {
    return {
      insertGallery:
        (attrs = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              images: attrs.images || [],
              mode: attrs.mode || 'wide',
            },
          })
        },
      updateGalleryImages:
        (images: GalleryImage[]) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, { images })
        },
    }
  },
})
